import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ROUTES } from "../../../constants/routes-path";
import { parkingCredentialsService } from "../../../services/parkingCredentialsService";
import { vehicleService } from "../../../services/vehicleService";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    LightMode, Select,
    Stack,
    Text, useColorModeValue
} from "@chakra-ui/react";
import CardHeader from "../../../components/Card/CardHeader";
import Loading from "../../../components/Loading/Loading";
import { SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import {
    checkErrors,
    hasError,
    isValidCEP,
    maskCPF,
    isValidVehiclePlate,
    plateMask
} from "../../../utils/forms-utils";
import {
    validateCPF,
    formatCEP,
    fetchAddressByCEP,
    formatPhoneNumber, ufs
} from "../../../utils";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import { FileAttachmentInput } from "../../../components/FileAttachmentInput/FileAttachmentInput";
import { blobToFile } from "./Documents/ParkingCredentialsUtils";
import WebcamCapture from "../../../components/WebcamCapture/WebcamCapture";
import YearMaskedInput from "../../../components/YearInput/YearMaskedInput";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

const ParkingCredentialsNew = ({ initialData = null, onSubmit = null, isEdit = false, loading = false, id = null }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const cardSpacing = "20px";
    const formSpacing = "30px";
    const translationKey = "parking-credentials";
    const formTranslationKey = `${translationKey}.form`;
    const formDirection = { sm: "column", md: "row" };
    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});
    const dialogRef = useRef(undefined);
    const attachmentRef = useRef(undefined);
    const [fileAttachmentName, setAttachmentFileName] = useState(null);
    const [initialAttachmentFile, setInitialAttachmentFile] = useState(null);
    const [address, setAddress] = useState({
        logradouro: "",
        bairro: "",
        localidade: "",
        uf: "",
    });
    const [croppedImage, setCroppedImage] = useState(null);

    const handleImageCaptured = (imgBase64) => {
        setCroppedImage(imgBase64);
    };

    const ids = useMemo(
        () => ({
            placaVeiculo: "vehicle-plate",
            ufPlaca: "vehicle-uf-plate",
            veiculoAnoFab: "vehicle-fabrication-year",
            veiculoAnoModelo: "vehicle-model-year",
            veiculoMarcaModelo: "vehicle-model",
            corVeiculo: "vehicle-color",
            chassiVeiculo: "vehicle-chassis",
            renavamVeiculo: "vehicle-renavam",
            nomeBeneficiario: "grantee-name",
            dataNascBeneficiario: "grantee-birth",
            identidadeBeneficiario: "identidade-beneficiario",
            orgaoExpedidorIdentidade: "id-expeditioner",
            ufIdentidade: "id-uf",
            cpfBeneficiario: "grantee-cpf",
            cnhBeneficiario: "grantee-cnh",
            cnhCategoria: "grantee-cnh-category",
            cnhValid: "grantee-cnh-valid",
            cepBeneficiario: "grantee-cep",
            logradouroBeneficiario: "grantee-street",
            logradouroNumeroBeneficiario: "grantee-street-number",
            logradouroMunicipioBeneficiario: "grantee-address-city",
            logradouroEstadoBeneficiario: "grantee-address-state",
            telefoneFixo: "grantee-landline",
            telefoneCelular: "grantee-cellphone",
            logradouroBairroBeneficiario: "grantee-address-neighbour"
        }),
        []
    );

    const initFormData = () => {
        setAddress((prev) => ({
            ...prev,
            logradouro: initialData?.logradouro_beneficiario || "",
            bairro: initialData?.bairro_beneficiario || "",
            localidade: initialData?.municipio_beneficiario || "",
            uf: initialData?.uf_beneficiario || "",
        }));
        return {
            [ids.placaVeiculo]: {
                value: initialData?.placa_veiculo || "",
                validations: [],
            },
            [ids.ufPlaca]: {
                value: initialData?.uf_placa_veiculo || "",
                validations: [],
            },
            [ids.veiculoAnoModelo]: {
                value: initialData?.ano_modelo_veiculo || "",
                validations: [],
            },
            [ids.veiculoAnoFab]: {
                value: initialData?.ano_fabricacao_veiculo || "",
                validations: [],
            },
            [ids.veiculoMarcaModelo]: {
                value: initialData?.descricao_marca_modelo_veiculo || "",
                validations: [],
            },
            [ids.corVeiculo]: {
                value: initialData?.descricao_cor_veiculo || "",
                validations: [],
            },
            [ids.renavamVeiculo]: {
                value: initialData?.numero_renavam_veiculo || "",
                validations: [],
            },
            [ids.chassiVeiculo]: {
                value: initialData?.numero_chassi_veiculo || "",
                validations: [],
            },
            [ids.identidadeBeneficiario]: {
                value: initialData?.identidade_beneficiario || "",
                validations: [{ required: true }],
            },
            [ids.orgaoExpedidorIdentidade]: {
                value: initialData?.orgao_expedidor_identidade_beneficiario || "",
                validations: [{ required: true }],
            },
            [ids.nomeBeneficiario]: {
                value: initialData?.nome_beneficiario || "",
                validations: [{ required: true }],
            },
            [ids.dataNascBeneficiario]: {
                value: initialData?.data_nascimento_beneficiario?.split('/').reverse().join('-') || new Date().toISOString().split("T")[0],
                validations: [{ required: true }],
            },
            [ids.ufIdentidade]: {
                value: initialData?.uf_identidade_beneficiario || "",
                validations: [{ required: true }],
            },
            [ids.cpfBeneficiario]: {
                value: maskMap[ids.cpfBeneficiario] ? maskMap[ids.cpfBeneficiario](initialData?.cpf_beneficiario || "") : (initialData?.cpf_beneficiario || ""),
                validations: [{ required: true }],
            },
            [ids.cnhBeneficiario]: {
                value: initialData?.cnh_beneficiario || "",
                validations: [],
            },
            [ids.cnhCategoria]: {
                value: initialData?.categoria_cnh_beneficiario || "",
                validations: [],
            },
            [ids.cnhValid]: {
                value: initialData?.validade_cnh_beneficiario?.split('/').reverse().join('-') || "",
                validations: [],
            },
            [ids.cepBeneficiario]: {
                value: maskMap[ids.cepBeneficiario] ? maskMap[ids.cepBeneficiario](initialData?.cep_beneficiario || "") : (initialData?.cep_beneficiario || ""),
                validations: [{ required: true }],
            },
            [ids.logradouroNumeroBeneficiario]: {
                value: initialData?.numero_beneficiario || "",
                validations: [{ required: true }],
            },
            [ids.telefoneFixo]: {
                value: maskMap[ids.telefoneFixo] ? maskMap[ids.telefoneFixo](initialData?.telefone_fixo_beneficiario || "") : (initialData?.telefone_fixo_beneficiario || ""),
                validations: [],
            },
            [ids.telefoneCelular]: {
                value: maskMap[ids.telefoneCelular] ? maskMap[ids.telefoneCelular](initialData?.telefone_celular_beneficiario || "") : (initialData?.telefone_celular_beneficiario || ""),
                validations: [],
            },
        };
    };

    const maskMap = {
        [ids.placaVeiculo]: plateMask,
        [ids.cpfBeneficiario]: maskCPF,
        [ids.telefoneFixo]: formatPhoneNumber,
        [ids.telefoneCelular]: formatPhoneNumber,
        [ids.cepBeneficiario]: formatCEP,
    };

    const validatorMap = {
        [ids.placaVeiculo]: {
            validate: (v) => !v || isValidVehiclePlate(v),
            errorKey: 'vehicle-plate-format',
        },
        [ids.cpfBeneficiario]: {
            validate: validateCPF,
            errorKey: 'cpf-format',
        },
        [ids.telefoneFixo]: {
            validate: (v) => v.length >= 15 || v.length == 0,
            errorKey: 'phone-number-invalid',
        },
        [ids.telefoneCelular]: {
            validate: (v) => v.length >= 15 || v.length == 0,
            errorKey: 'phone-number-invalid',
        },
        [ids.cepBeneficiario]: {
            validate: isValidCEP,
            errorKey: 'cep-format',
        },
    };

    const toFormData = () => {
        const formValues = {
            placa_veiculo: formData[ids.placaVeiculo].value.trim(),
            uf_placa_veiculo: formData[ids.ufPlaca].value.trim(),
            descricao_marca_modelo_veiculo: formData[ids.veiculoMarcaModelo].value || "",
            ano_fabricacao_veiculo: formData[ids.veiculoAnoFab].value,
            ano_modelo_veiculo: formData[ids.veiculoAnoModelo].value,
            descricao_cor_veiculo: formData[ids.corVeiculo].value.trim(),
            numero_chassi_veiculo: formData[ids.chassiVeiculo].value.trim(),
            numero_renavam_veiculo: formData[ids.renavamVeiculo].value.trim(),
            nome_beneficiario: formData[ids.nomeBeneficiario].value.trim(),
            data_nascimento_beneficiario: formData[ids.dataNascBeneficiario].value.split('-').reverse().join('/'),
            identidade_beneficiario: formData[ids.identidadeBeneficiario].value.trim(),
            orgao_expedidor_identidade_beneficiario: formData[ids.orgaoExpedidorIdentidade].value.trim(),
            uf_identidade_beneficiario: formData[ids.ufIdentidade].value.trim(),
            cpf_beneficiario: formData[ids.cpfBeneficiario].value.trim(),
            cnh_beneficiario: formData[ids.cnhBeneficiario].value.trim(),
            categoria_cnh_beneficiario: formData[ids.cnhCategoria].value.trim(),
            validade_cnh_beneficiario: formData[ids.cnhValid].value.split('-').reverse().join('/'),
            cep_beneficiario: formData[ids.cepBeneficiario].value.trim(),
            logradouro_beneficiario: address.logradouro || formData[ids.logradouroBeneficiario].value.trim(),
            numero_beneficiario: formData[ids.logradouroNumeroBeneficiario].value.trim(),
            bairro_beneficiario: address.bairro || formData[ids.logradouroBairroBeneficiario].value.trim(),
            municipio_beneficiario: address.localidade || formData[ids.logradouroMunicipioBeneficiario].value.trim(),
            uf_beneficiario: address.uf || formData[ids.logradouroEstadoBeneficiario].value.trim(),
            telefone_fixo_beneficiario: formData[ids.telefoneFixo].value.trim(),
            telefone_celular_beneficiario: formData[ids.telefoneCelular].value.trim()
        };

        const data = new FormData();

        data.append("requestData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));
        if (attachmentRef?.current?.getFile()) {
            data.append("foto", attachmentRef.current.getFile().file, attachmentRef.current.getFile().name);
        }
        if (croppedImage){
            data.append("base64Webcam", croppedImage);
        }

        return data;
    };

    const handleSave = async () => {
        if (isFormInvalid) return;

        try {
            const data = toFormData();
            if (isEdit && onSubmit) {
                await onSubmit(data);
            } else {
                await parkingCredentialsService.create(data);
                showToast({
                    title: t("process.messages.success.create"),
                    status: "success",
                });
                redirectToList();
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const isFormInvalid = (() => {
        const addressFormData = {
            ...formData,
            [ids.logradouroBeneficiario]: {
                value: address.logradouro || "",
                validations: [{ required: true }],
            },
            [ids.logradouroBairroBeneficiario]: {
                value: address.bairro || "",
                validations: [{ required: true }],
            },
            [ids.logradouroMunicipioBeneficiario]: {
                value: address.localidade || "",
                validations: [{ required: true }],
            },
            [ids.logradouroEstadoBeneficiario]: {
                value: address.uf || "",
                validations: [{ required: true }],
            },
        };

        return formData && inputError ? hasError({ formData: addressFormData, inputError }) : true;
    })();

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.parkingCredentials);
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        const newValue =
            (id === ids.dataNascBeneficiario || id === ids.cnhValid)
                ? (moment(value).isValid() ? moment(value).format(DATE_FORMAT_ISO) : "")
                : (maskMap[id] ? maskMap[id](value) : value);

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: newValue },
        };

        setFormData(newFormData);

        const validator = validatorMap[id];
        let formValidationErrors = checkErrors({ formData: newFormData, inputError: inputError, name: id })

        if (validator) {
            if (!validator.validate(newValue)) {
                formValidationErrors[id] = t(`${translationKey}.messages.errors.${validator.errorKey}`);
            } else {
                delete formValidationErrors[id];
            }
        }

        setInputError(formValidationErrors);
    };

    const handleChangeCEP = async (e) => {
        handleInputChange(e)
        const formatted = formatCEP(e.target.value);
        if (formatted.length === 9) {
            fetchAddressByCEP(formatted.replace("-", ""), setAddress);
        }
    };

    const validateAddressField = (value) => {
        return !value?.trim() ? t("system.messages.errors.required-field") : null;
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setAddress((prev) => ({ ...prev, [name]: value }));

        const fieldId = {
            logradouro: ids.logradouroBeneficiario,
            bairro: ids.logradouroBairroBeneficiario,
            localidade: ids.logradouroMunicipioBeneficiario,
            uf: ids.logradouroEstadoBeneficiario,
        }[name];

        if (!fieldId) return;

        setInputError(prev => {
            const errorMsg = validateAddressField(value);
            if (errorMsg) {
                return { ...prev, [fieldId]: errorMsg };
            }
            const { [fieldId]: _, ...rest } = prev;
            return rest;
        });
    };

    const handleVehiclePlateSearch = async () => {
        try {
            const result = await vehicleService.findByPlate(
                formData[ids.placaVeiculo].value
            );
            // TODO: verificar se há alguma configuração no backend trazendo os dados em camelCase ao invés de snake_case.
            const veiculoData = {
                [ids.veiculoAnoFab]: {
                    ...formData[ids.veiculoAnoFab],
                    value: result.anoFabricacao,
                },
                [ids.veiculoAnoModelo]: {
                    ...formData[ids.veiculoAnoModelo],
                    value: result.anoModelo,
                },
                [ids.renavamVeiculo]: {
                    ...formData[ids.renavamVeiculo],
                    value: result.numeroRenavam,
                },
                [ids.corVeiculo]: {
                    ...formData[ids.corVeiculo],
                    value: result.descricaoCor,
                },
                [ids.chassiVeiculo]: {
                    ...formData[ids.chassiVeiculo],
                    value: result.numeroChassi,
                },
                [ids.veiculoMarcaModelo]: {
                    ...formData[ids.veiculoMarcaModelo],
                    value: result.descricaoMarcaModelo,
                },
                [ids.ufPlaca]: {
                    ...formData[ids.ufPlaca],
                    value: result.ufPlaca,
                }
            };

            setFormData((prev) => ({ ...prev, ...veiculoData }));
            setInputError(checkErrors({ formData: veiculoData, inputError }));
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleConfirmDeleteAttachment = async () => {
        if (id) {
            try {
                await parkingCredentialsService.removeAttachment(id);
                fetchData()
            } catch (err) {
                showToast({ title: t(err.message), status: "error" });
            }
        }
    };

    const fetchData = async () => {
        try {
            if (id) {
                try {
                    const attachment = await parkingCredentialsService.attachment(id);
                    setInitialAttachmentFile(blobToFile(attachment, id));
                } catch (err) {
                    setInitialAttachmentFile(null);
                }
            }
            setFormData(initFormData());
        } catch (err) {
            showToast({ title: t(err.message), status: "error" });
            redirectToList();
        }
    };

    useEffect(() => {
        if (isEdit && !loading && initialData) {
            fetchData();
            return;
        }
        if (!isEdit) {
            fetchData();
        }

    }, [loading, initialData]);

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>

            {/*Vehicle card*/}
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <CardHeader mb="20px">
                    <Text color={textColor} fontSize="lg" fontWeight="bold">
                        {t(`${formTranslationKey}.title.vehicle-data`)}
                    </Text>
                </CardHeader>
                <CardBody>
                    {formData && !loading ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            {/*vehicle-plate*/}
                            <Stack alignSelf="flex-start" spacing={formSpacing} direction="row" minW="50%" alignItems="flex-start">
                                <FormControl isRequired isInvalid={inputError[ids.placaVeiculo]}>
                                    <Input
                                        fontSize="sm"
                                        id={ids.placaVeiculo}
                                        name={ids.placaVeiculo}
                                        value={formData[ids.placaVeiculo].value.toUpperCase()}
                                        onChange={handleInputChange}
                                        placeholder={t(`${formTranslationKey}.fields.vehicle-plate.placeholder`)}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.placaVeiculo])}
                                    </FormErrorMessage>
                                </FormControl>

                                <LightMode>
                                    <Button
                                        variant="dark"
                                        fontSize="sm"
                                        isDisabled={
                                            inputError[ids.placaVeiculo] || !formData[ids.placaVeiculo].value
                                        }
                                        leftIcon={<SearchIcon />}
                                        onClick={handleVehiclePlateSearch}
                                        minW="fit-content"
                                        _hover={isFormInvalid ? {} : undefined}
                                    >
                                        <Text>
                                            {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                                        </Text>
                                    </Button>
                                </LightMode>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>

                                {/*vehicle-uf-plate*/}
                                <FormControl isInvalid={inputError[ids.ufPlaca]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-uf-plate.label`)}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        variant="main"
                                        id={ids.ufPlaca}
                                        placeholder={t(`${formTranslationKey}.fields.vehicle-uf-plate.placeholder`)}
                                        value={formData[ids.ufPlaca].value}
                                        onChange={handleInputChange}
                                    >
                                        {ufs.map((uf) => (
                                            <option key={uf.value} value={uf.value}>
                                                {uf.value} - {uf.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {t(inputError[ids.ufPlaca])}
                                    </FormErrorMessage>
                                </FormControl>

                                {/*vehicle-model*/}
                                <FormControl isInvalid={inputError[ids.veiculoMarcaModelo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-model.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.veiculoMarcaModelo}
                                        value={formData[ids.veiculoMarcaModelo].value}
                                        onChange={handleInputChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.veiculoMarcaModelo])}
                                    </FormErrorMessage>
                                </FormControl>

                                {/*vehicle-fabrication-year*/}
                                <YearMaskedInput
                                    id={ids.veiculoAnoFab}
                                    label={`${formTranslationKey}.fields.vehicle-fabrication-year.label`}
                                    value={formData[ids.veiculoAnoFab].value}
                                    onChange={handleInputChange}
                                    error={inputError[ids.veiculoAnoFab]}
                                />

                                {/*vehicle-model-year*/}
                                <YearMaskedInput
                                    id={ids.veiculoAnoModelo}
                                    label={`${formTranslationKey}.fields.vehicle-model-year.label`}
                                    value={formData[ids.veiculoAnoModelo].value}
                                    onChange={handleInputChange}
                                    error={inputError[ids.veiculoAnoModelo]}
                                />
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                {/*cor_veiculo*/}
                                <FormControl isInvalid={inputError[ids.corVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-color.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.corVeiculo}
                                        value={formData[ids.corVeiculo].value}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>

                                {/*chassi_veiculo*/}
                                <FormControl>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-chassis.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.chassiVeiculo}
                                        value={formData[ids.chassiVeiculo].value}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>

                                {/*renavam_veiculo*/}
                                <FormControl isInvalid={inputError[ids.renavamVeiculo]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${formTranslationKey}.fields.vehicle-renavam.label`)}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.renavamVeiculo}
                                        value={formData[ids.renavamVeiculo].value}
                                        onChange={handleInputChange}
                                    />
                                </FormControl>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>

            <Stack w="100%" direction={{ sm: "column", md: "row" }} spacing={cardSpacing}>
                {/*Grantee card*/}
                <Card flex="1" minW="0" overflowX={{ sm: "scroll", lg: "hidden" }} minH="49vh">
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.grantee-data`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        {
                            formData && !loading ? (
                                <Stack direction="column">
                                    {/*nome_beneficiario*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.nomeBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-name.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.nomeBeneficiario}
                                            value={formData[ids.nomeBeneficiario].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.nomeBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*data_nasc_beneficiario*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.dataNascBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-birth.label`)}
                                        </FormLabel>
                                        <DateTime
                                            dateFormat={DATE_FORMAT}
                                            timeFormat={false}
                                            locale={LOCALE_PT_BR}
                                            value={
                                                formData[ids.dataNascBeneficiario].value
                                                    ? moment(formData[ids.dataNascBeneficiario].value, DATE_FORMAT_ISO)
                                                    : ""
                                            }
                                            renderInput={(props) => (
                                                <Input
                                                    {...props}
                                                    fontSize="sm"
                                                    variant="main"
                                                    id={ids.dataNascBeneficiario}
                                                />
                                            )}
                                            onChange={(date) =>
                                                handleInputChange({
                                                    target: { id: ids.dataNascBeneficiario, value: date }
                                                })
                                            }
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.dataNascBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*num_identidade*/}
                                    <FormControl isRequired isInvalid={inputError[ids.identidadeBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-id.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.identidadeBeneficiario}
                                            value={formData[ids.identidadeBeneficiario].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.identidadeBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*orgao_expedidor_identidade*/}
                                    <FormControl isRequired isInvalid={inputError[ids.orgaoExpedidorIdentidade]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.id-expeditioner.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.orgaoExpedidorIdentidade}
                                            value={formData[ids.orgaoExpedidorIdentidade].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.orgaoExpedidorIdentidade])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*uf_identidade*/}
                                    <FormControl isRequired isInvalid={inputError[ids.ufIdentidade]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.id-uf.label`)}
                                        </FormLabel>
                                        <Select
                                            fontSize="sm"
                                            variant="main"
                                            id={ids.ufIdentidade}
                                            placeholder={t(`${formTranslationKey}.fields.vehicle-uf-plate.placeholder`)}
                                            value={formData[ids.ufIdentidade].value}
                                            onChange={handleInputChange}
                                        >
                                            {ufs.map((uf) => (
                                                <option key={uf.value} value={uf.value}>
                                                    {uf.value} - {uf.label}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.ufIdentidade])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*cpf_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.cpfBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cpf.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cpfBeneficiario}
                                            maxLength="14"
                                            value={formData[ids.cpfBeneficiario].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cpfBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*cnh_beneficiario*/}
                                    <FormControl isInvalid={inputError[ids.cnhBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cnh.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cnhBeneficiario}
                                            value={formData[ids.cnhBeneficiario].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cnhBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*categoria_cnh*/}
                                    <FormControl isInvalid={inputError[ids.cnhCategoria]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cnh-category.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cnhCategoria}
                                            value={formData[ids.cnhCategoria].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cnhCategoria])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*validade_cnh*/}
                                    <FormControl isInvalid={inputError[ids.cnhValid]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cnh-valid.label`)}
                                        </FormLabel>
                                        <DateTime
                                            dateFormat={DATE_FORMAT}
                                            timeFormat={false}
                                            locale={LOCALE_PT_BR}
                                            value={
                                                formData[ids.cnhValid].value
                                                    ? moment(formData[ids.cnhValid].value, DATE_FORMAT_ISO)
                                                    : ""
                                            }
                                            renderInput={(props) => (
                                                <Input
                                                    {...props}
                                                    fontSize="sm"
                                                    variant="main"
                                                    id={ids.cnhValid}
                                                />
                                            )}
                                            onChange={(date) =>
                                                handleInputChange({
                                                    target: { id: ids.cnhValid, value: date }
                                                })
                                            }
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cnhValid])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*foto*/}
                                    <FileAttachmentInput
                                        ref={attachmentRef}
                                        nameVariable={fileAttachmentName}
                                        acceptedTypes={['image/jpeg']}
                                        translationKey={translationKey}
                                        initialFile={initialAttachmentFile}
                                        onConfirmDelete={handleConfirmDeleteAttachment}
                                    />

                                    {/*webcam*/}
                                    <LightMode>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.webcam.label`)}
                                        </FormLabel>

                                        <WebcamCapture onCapture={handleImageCaptured} />
                                    </LightMode>

                                </Stack>
                            ) : (
                                <Loading h="300px" w="100%" />
                            )
                        }
                    </CardBody>
                </Card>

                <Card flex="1" minW="0" overflowX={{ sm: "scroll", lg: "hidden" }} minH="49vh">
                    <CardHeader mb="20px">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${formTranslationKey}.title.grantee-info`)}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        {
                            formData && !loading ? (
                                <Stack direction="column">
                                    {/*cep_beneficiario*/}
                                    <FormControl isRequired isInvalid={!!inputError[ids.cepBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cep.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.cepBeneficiario}
                                            name="cep"
                                            maxLength={9}
                                            value={formData[ids.cepBeneficiario].value}
                                            onChange={handleChangeCEP}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.cepBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*logradouro_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.logradouroBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-street.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.logradouroBeneficiario}
                                            name="logradouro"
                                            value={address.logradouro}
                                            onChange={handleAddressChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.logradouroBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*numero_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.logradouroNumeroBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-street-number.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.logradouroNumeroBeneficiario}
                                            name="numero"
                                            value={formData[ids.logradouroNumeroBeneficiario].value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.logradouroNumeroBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*bairro_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.logradouroBairroBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-address-neighbour.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.logradouroBairroBeneficiario}
                                            name="bairro"
                                            value={address.bairro}
                                            onChange={handleAddressChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.logradouroBairroBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*cidade_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.logradouroMunicipioBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-address-city.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.logradouroMunicipioBeneficiario}
                                            name="localidade"
                                            value={address.localidade}
                                            onChange={handleAddressChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.logradouroMunicipioBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*uf_beneficiario*/}
                                    <FormControl isRequired isInvalid={inputError[ids.logradouroEstadoBeneficiario]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-address-state.label`)}
                                        </FormLabel>
                                        <Select
                                            fontSize="sm"
                                            variant="main"
                                            id={ids.logradouroEstadoBeneficiario}
                                            name="uf"
                                            placeholder={t(`${formTranslationKey}.fields.grantee-address-state.placeholder`)}
                                            value={address.uf.toUpperCase()}
                                            onChange={handleAddressChange}
                                        >
                                            {ufs.map((uf) => (
                                                <option key={uf.value} value={uf.value}>
                                                    {uf.value} - {uf.label}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormErrorMessage>
                                            {t(inputError[ids.logradouroEstadoBeneficiario])}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*telefone_fixo*/}
                                    <FormControl isInvalid={!!inputError[ids.telefoneFixo]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-landline.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.telefoneFixo}
                                            value={formData[ids.telefoneFixo].value}
                                            onChange={handleInputChange}
                                            maxLength={15}
                                        />
                                        <FormErrorMessage>
                                            {inputError[ids.telefoneFixo]}
                                        </FormErrorMessage>
                                    </FormControl>

                                    {/*telefone_celular*/}
                                    <FormControl isInvalid={!!inputError[ids.telefoneCelular]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t(`${formTranslationKey}.fields.grantee-cellphone.label`)}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.telefoneCelular}
                                            value={formData[ids.telefoneCelular].value}
                                            onChange={handleInputChange}
                                            maxLength={15}
                                        />
                                        <FormErrorMessage>
                                            {inputError[ids.telefoneCelular]}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Stack>
                            ) : (<Loading h="300px" w="100%" />)
                        }
                    </CardBody>
                </Card>
            </Stack>
            <Stack w="100%" direction="row" justify="space-between">
                <Button
                    w="125px"
                    variant="no-effects"
                    bg="gray.100"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="bold"
                    onClick={redirectToList}
                >
                    {t("system.actions.cancel")}
                </Button>

                <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    isDisabled={isFormInvalid}
                    onClick={handleSave}
                    _hover={isFormInvalid ? {} : undefined}
                >
                    {t(isEdit ? "system.actions.edit" : "system.actions.save")}
                </Button>
            </Stack>
            <ConfirmDialog ref={dialogRef} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default ParkingCredentialsNew;
